import React, { useState, useEffect } from 'react';
import { FaCalendarPlus, FaTrash, FaSync } from 'react-icons/fa';
import api from '../services/api';

const BillingRuns = () => {
    const [billingRuns, setBillingRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Panel generowania
    const currentDate = new Date();
    const [generateYear, setGenerateYear] = useState(currentDate.getFullYear());
    const [generateMonth, setGenerateMonth] = useState(currentDate.getMonth() + 1);
    const [generating, setGenerating] = useState(false);

    // Modal potwierdzenia usunięcia
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const monthNames = [
        'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
        'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
    ];

    const translateError = (msg) => {
        if (!msg) return msg;
        if (/already completed/i.test(msg)) return msg.replace(/Billing for (\S+) already completed/i, 'Faktury za $1 zostały już wygenerowane');
        if (/already in progress/i.test(msg)) return msg.replace(/Billing for (\S+) already in progress/i, 'Generowanie faktur za $1 jest już w toku');
        if (/Cannot delete billing run in progress/i.test(msg)) return 'Nie można usunąć generowania w toku';
        if (/Billing run for .+ not found/i.test(msg)) return msg.replace(/Billing run for (\S+) not found/i, 'Nie znaleziono generowania za $1');
        if (/Cannot generate invoices for future/i.test(msg)) return 'Nie można wygenerować faktur za przyszły okres';
        return msg;
    };

    const fetchBillingRuns = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/invoices/billing-runs');
            setBillingRuns(response.data);
        } catch (error) {
            console.error('Failed to fetch billing runs', error);
            setError('Nie udało się pobrać listy billing runs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBillingRuns();
    }, []);

    const handleGenerate = async () => {
        try {
            setGenerating(true);
            setError(null);
            setSuccessMessage('');
            await api.post(`/invoices/generate-monthly?year=${generateYear}&month=${generateMonth}`);
            setSuccessMessage(`Faktury za ${monthNames[generateMonth - 1]} ${generateYear} zostały wygenerowane`);
            await fetchBillingRuns();
        } catch (error) {
            console.error('Failed to generate invoices', error);
            const detail = error.response?.data?.detail;
            if (typeof detail === 'string') {
                setError(translateError(detail));
            } else if (Array.isArray(detail)) {
                setError(detail.map(e => e.msg).join(', '));
            } else {
                setError('Nie udało się wygenerować faktur');
            }
        } finally {
            setGenerating(false);
        }
    };

    const handleDelete = async (year, month) => {
        try {
            setDeleting(true);
            setError(null);
            setSuccessMessage('');
            await api.delete(`/invoices/billing-runs/${year}/${month}`);
            setSuccessMessage(`Billing run za ${monthNames[month - 1]} ${year} został usunięty`);
            setDeleteConfirm(null);
            await fetchBillingRuns();
        } catch (error) {
            console.error('Failed to delete billing run', error);
            const detail = error.response?.data?.detail;
            if (typeof detail === 'string') {
                setError(translateError(detail));
            } else {
                setError('Nie udało się usunąć generowania faktur');
            }
        } finally {
            setDeleting(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('pl-PL');
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'pending': { label: 'W toku', color: 'bg-yellow-200 text-yellow-800' },
            'completed': { label: 'Zakończony', color: 'bg-green-200 text-green-800' },
            'failed': { label: 'Błąd', color: 'bg-red-200 text-red-800' },
        };
        const statusInfo = statusMap[status] || { label: status, color: 'bg-gray-200 text-gray-700' };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
                {statusInfo.label}
            </span>
        );
    };

    const renderDeleteModal = () => {
        if (!deleteConfirm) return null;
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
                    <h3 className="text-xl font-bold mb-4">Potwierdzenie usunięcia</h3>
                    <p className="mb-6 text-gray-600">
                        Czy na pewno chcesz usunąć billing run za{' '}
                        <strong>{monthNames[deleteConfirm.month - 1]} {deleteConfirm.year}</strong>?
                        Spowoduje to usunięcie wszystkich faktur z tego okresu.
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={() => handleDelete(deleteConfirm.year, deleteConfirm.month)}
                            disabled={deleting}
                            className="flex-1 bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition disabled:opacity-50"
                        >
                            {deleting ? 'Usuwanie...' : 'Usuń'}
                        </button>
                        <button
                            onClick={() => setDeleteConfirm(null)}
                            disabled={deleting}
                            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition disabled:opacity-50"
                        >
                            Anuluj
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderMobileList = () => {
        return billingRuns.map((run, index) => (
            <div key={index} className="mb-4 p-4 border border-gray-200 rounded-lg bg-white shadow-sm">
                <div className="flex justify-between items-start mb-3">
                    <div>
                        <p className="font-bold text-lg">{monthNames[run.month - 1]} {run.year}</p>
                    </div>
                    {getStatusBadge(run.status)}
                </div>
                <div className="mb-2">
                    <span className="text-gray-600 text-sm">Utworzono:</span>
                    <p>{formatDateTime(run.created_at)}</p>
                </div>
                {run.error && (
                    <div className="mb-2">
                        <span className="text-gray-600 text-sm">Błąd:</span>
                        <p className="text-red-600 text-sm">{run.error}</p>
                    </div>
                )}
                <div className="flex justify-end">
                    <button
                        onClick={() => setDeleteConfirm({ year: run.year, month: run.month })}
                        className="text-red-500 hover:text-red-700 p-2"
                        title="Usuń"
                    >
                        <FaTrash />
                    </button>
                </div>
            </div>
        ));
    };

    const renderDesktopTable = () => {
        return (
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="border px-4 py-2 text-left">Rok</th>
                            <th className="border px-4 py-2 text-left">Miesiąc</th>
                            <th className="border px-4 py-2 text-center">Status</th>
                            <th className="border px-4 py-2 text-left">Utworzono</th>
                            <th className="border px-4 py-2 text-left">Błąd</th>
                            <th className="border px-4 py-2 text-center">Usuń</th>
                        </tr>
                    </thead>
                    <tbody>
                        {billingRuns.map((run, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                                <td className="border px-4 py-2">{run.year}</td>
                                <td className="border px-4 py-2">{monthNames[run.month - 1]}</td>
                                <td className="border px-4 py-2 text-center">{getStatusBadge(run.status)}</td>
                                <td className="border px-4 py-2">{formatDateTime(run.created_at)}</td>
                                <td className="border px-4 py-2 text-red-600 text-sm">{run.error || '-'}</td>
                                <td className="border px-4 py-2 text-center">
                                    <button
                                        onClick={() => setDeleteConfirm({ year: run.year, month: run.month })}
                                        className="text-red-500 hover:text-red-700 p-1"
                                        title="Usuń"
                                    >
                                        <FaTrash />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="min-h-screen p-4">
            <main className="container mx-auto">
                {/* Panel generowania */}
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg mb-6">
                    <h2 className="text-xl sm:text-2xl font-bold flex items-center mb-4">
                        <FaCalendarPlus className="mr-2 text-softTeal" />
                        Generowanie faktur
                    </h2>

                    <div className="flex flex-col sm:flex-row gap-4 items-end">
                        <div>
                            <label className="block text-gray-700 text-sm mb-1">Rok</label>
                            <select
                                value={generateYear}
                                onChange={(e) => setGenerateYear(Number(e.target.value))}
                                className="p-2 border border-gray-300 rounded-lg"
                            >
                                {[currentDate.getFullYear() - 1, currentDate.getFullYear(), currentDate.getFullYear() + 1].map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm mb-1">Miesiąc</label>
                            <select
                                value={generateMonth}
                                onChange={(e) => setGenerateMonth(Number(e.target.value))}
                                className="p-2 border border-gray-300 rounded-lg"
                            >
                                {monthNames.map((name, i) => (
                                    <option key={i + 1} value={i + 1}>{name}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            onClick={handleGenerate}
                            disabled={generating}
                            className="bg-softTeal text-white font-semibold px-6 py-2 rounded-lg hover:bg-warmOrange transition disabled:opacity-50 flex items-center"
                        >
                            {generating ? (
                                <>
                                    <FaSync className="mr-2 animate-spin" />
                                    Generowanie...
                                </>
                            ) : (
                                <>
                                    <FaCalendarPlus className="mr-2" />
                                    Generuj
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Komunikaty */}
                {successMessage && (
                    <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                        {successMessage}
                    </div>
                )}
                {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                        {error}
                    </div>
                )}

                {/* Lista billing runs */}
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg">
                    <h3 className="text-lg font-bold mb-4">Historia generowania</h3>

                    {loading ? (
                        <p className="text-center py-4">Ładowanie...</p>
                    ) : billingRuns.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">Brak billing runs</p>
                    ) : (
                        <>
                            <div className="md:hidden">
                                {renderMobileList()}
                            </div>
                            <div className="hidden md:block">
                                {renderDesktopTable()}
                            </div>
                        </>
                    )}
                </div>
            </main>

            {renderDeleteModal()}
        </div>
    );
};

export default BillingRuns;
